import axios from 'axios';
import Auth from '../auth/auth';
import SessionManager from '../auth/session-manager';

// docs/access/slice-3b-plan.md §3.3 — the interceptor's OWN SessionManager. SessionGuard's instance
// lives on a mounted component and is unreachable from a plain module; this one is harmless because
// refreshAuthToken is fetch-based and stateless beyond the token it reads/writes in localStorage.
// Deliberately NOT the default axios instance — routing the refresh through axios would let its own
// response re-enter this interceptor.
const sessionManager = new SessionManager(new Auth());

const TERMINAL_CODES = new Set(['SESSION_REVOKED', 'SESSION_EXPIRED']);

let refreshPromise = null;

// Every one of the ~41 call sites bakes the token into config.headers['x-access-token'] before the
// request goes out, so the captured error.config still carries the EXPIRED one. Overwriting it here
// is what a bare `axios(error.config)` would skip.
const stampToken = (config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers = {...config.headers, 'x-access-token': token};
  }
  return config;
};

/**
 * One global response interceptor on the default axios instance (§3.3). All ~41 call sites do
 * `import axios from 'axios'` and share this default instance, so registering here once — from
 * src/index.js — covers every one of them with no call-site migration.
 *
 * @return {number} The interceptor id, from axios.interceptors.response.use.
 */
export const registerHttpInterceptor = () => axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const response = error.response;
      if (!response || response.status !== 401) {
        return Promise.reject(error);
      }

      const code = response.data && response.data.code;

      if (TERMINAL_CODES.has(code)) {
        // Terminal, never retried — retrying a revoked session is the refresh-loop this slice must
        // not ship. There is no Redux logout action; Auth.logout() is the house mechanism.
        await new Auth().logout();
        return Promise.reject(error);
      }

      if (code !== 'TOKEN_EXPIRED') {
        return Promise.reject(error);
      }

      const config = error.config;
      // Per-request retry marker, carried onto the stamped replay below. Single-flight alone does
      // NOT stop recursion: each cycle's refresh resolves successfully, so a replay that 401s
      // TOKEN_EXPIRED again would refresh again, forever — reachable with no attacker, via a server
      // clock ahead of the client by more than the TTL.
      if (!config || config._retried) {
        await new Auth().logout();
        return Promise.reject(error);
      }
      config._retried = true;

      // Single-flight: concurrent 401s await one shared refresh promise rather than firing one
      // refresh per panel.
      if (!refreshPromise) {
        refreshPromise = sessionManager.refreshAuthToken().finally(() => {
          refreshPromise = null;
        });
      }
      const inFlight = refreshPromise;
      const refreshed = await inFlight;

      if (!refreshed) {
        await new Auth().logout();
        return Promise.reject(error);
      }

      // NOT axios(error.config) — every request released from the single-flight queue is stamped
      // with the new token the same way.
      return axios(stampToken(config));
    },
);
